import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import { useTranslation } from "react-i18next";
// 导入Swiper核心组件和必要模块
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import type { SwiperRef } from 'swiper/react';
// 导入Swiper样式
import 'swiper/css';
import 'swiper/css/navigation';

// Partners array with company logos
const partners = [
  { name: "Legal Vision", logo: "/lovable-uploads/ee7136db-f62c-417a-b6ed-aecb50b0e719.webp" },
  { name: "NewGen Lending Solutions", logo: "/lovable-uploads/a62c8002-cadb-4266-9f4d-522234c548f0.webp" },
  { name: "UNSW Founders", logo: "/lovable-uploads/cb2e6291-b332-4047-b4a5-8e8a9c17e565.webp" },
  { name: "Webzilla", logo: "/lovable-uploads/23095887-9f7c-4253-be2c-c9f0535f390a.webp" },
  { name: "Detection Agency", logo: "/lovable-uploads/0f4e293f-424f-4c50-a715-e4c5200145da.webp" },
  { name: "AWS", logo: "/lovable-uploads/2df706fe-0865-4bbe-badc-09304545107f.webp" }
];

// Fallback partner display if we can't load the actual partners
const fallbackPartner = { name: "Partner", logo: "" };

const ProfessionalPartnersSection = () => {
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);
  const [loadedPartners, setLoadedPartners] = useState<typeof partners>([]);
  const [loading, setLoading] = useState(true);
  const swiperRef = useRef<SwiperRef>(null);
  
  // 加载CSS样式
  useEffect(() => {
    // 创建style元素
    const style = document.createElement('style');
    style.textContent = `
      /* 隐藏Swiper默认导航箭头 */
      .swiper-button-next::after,
      .swiper-button-prev::after {
        display: none;
      }
      
      /* 自定义Swiper样式 */
      .swiper-slide {
        height: auto;
      }
      
      /* 解决闪动问题 */
      .swiper-container {
        overflow: hidden;
      }
      
      /* 防止轮播重置时的闪动 */
      .partners-carousel .swiper-slide-duplicate {
        visibility: visible !important;
      }

      /* 自定义导航按钮鼠标样式 - 使用多种CSS选择器和特性确保应用 */
      .carousel-prev-button,
      .carousel-next-button {
        cursor: pointer !important;
      }
      
      /* 使用更具体的选择器来增加特异性 */
      div.carousel-prev-button,
      div.carousel-next-button {
        cursor: pointer !important;
      }
      
      /* 使用属性选择器 */
      [aria-label="Previous slide"],
      [aria-label="Next slide"] {
        cursor: pointer !important;
      }
      
      /* 对Swiper的原生导航按钮也应用相同的鼠标样式 */
      .swiper-button-prev,
      .swiper-button-next {
        cursor: pointer !important;
      }
    `;
    // 添加到head
    document.head.appendChild(style);
    
    // 清理函数
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // 加载合作伙伴并验证图片
  useEffect(() => {
    const checkAndLoadPartners = async () => {
      try {
        setLoading(true);
        if (partners.length > 0) {
          // 预加载所有合作伙伴图片
          const imageLoadPromises = partners.map(partner => {
            return new Promise<boolean>((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => reject(new Error(`Failed to load image for ${partner.name}`));
              img.src = partner.logo;
            });
          });

          try {
            const results = await Promise.allSettled(imageLoadPromises);
            const verifiedPartners = partners.filter((_, index) => results[index].status === 'fulfilled');
            if (verifiedPartners.length > 0) {
              setLoadedPartners(verifiedPartners);
              setIsError(false);
            } else {
              setIsError(true);
              setLoadedPartners([fallbackPartner, fallbackPartner, fallbackPartner]);
            }
          } catch (error) {
            console.warn("Some partner images failed to load:", error);
            setIsError(true);
            setLoadedPartners([fallbackPartner, fallbackPartner, fallbackPartner]);
          }
        } else {
          setIsError(true);
          setLoadedPartners([fallbackPartner, fallbackPartner, fallbackPartner]);
        }
      } catch (error) {
        console.error("Error loading partners:", error);
        setIsError(true);
        setLoadedPartners([fallbackPartner, fallbackPartner, fallbackPartner]);
      } finally {
        setLoading(false);
      }
    };
    
    checkAndLoadPartners();
  }, []);
  
  // 图片加载错误处理
  const handleImageError = (index: number) => {
    console.warn(`Failed to load image for partner: ${loadedPartners[index % loadedPartners.length].name}`);
    // 使用备用图片
    const updatedPartners = [...loadedPartners];
    updatedPartners[index % loadedPartners.length] = {
      ...updatedPartners[index % loadedPartners.length],
      logo: '/placeholder.svg' 
    };
    setLoadedPartners(updatedPartners);
  };
  
  // 如果出错或没有合作伙伴，则渲染回退界面
  if (isError || loadedPartners.length === 0) {
    return (
      <section className="py-20 bg-black/80">
        <div className="container mx-auto px-4">
          <SectionHeading
            title={t('partners.title') || "Our Partners"} 
            description={t('partners.description') || "Working with leading brands to bring you the best mystery experiences"} 
            centered={true}
          />
          <div className="flex flex-wrap justify-center gap-8 mt-12">
            {[1, 2, 3].map((_, index) => (
              <div 
                key={index}
                className="bg-black/30 rounded-lg p-6 flex items-center justify-center w-[240px] h-[160px]"
              >
                <span className="text-gray-400">Partner</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  
  // 创建更多项目以确保连续滚动
  const displayPartners = [...loadedPartners, ...loadedPartners, ...loadedPartners];
  
  return (
    <section className="py-20 bg-black/80">
      <div className="container mx-auto px-4">
        <SectionHeading
          title={t('partners.title') || "Our Partners"} 
          description={t('partners.description') || "Working with leading brands to bring you the best mystery experiences"} 
          centered={true}
        />
        
        <motion.div 
          className="mt-12 relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          {/* 使用Swiper实现专业轮播 */}
          <div className="px-16 relative mx-auto max-w-6xl">
            {/* 自定义导航按钮 - 放在Swiper外部，以便正确定位和处理事件 */}
            <div 
              className="carousel-prev-button absolute -left-4 top-1/2 -translate-y-1/2 w-12 h-14 flex items-center justify-center z-20 bg-black/40 rounded-lg hover:bg-black/60 transition-colors cursor-pointer"
              onClick={() => {
                // 直接使用DOM API触发点击事件确保正常工作
                const prevBtn = document.querySelector('.swiper-button-prev');
                if (prevBtn) {
                  (prevBtn as HTMLElement).click();
                }
              }}
              aria-label="Previous slide"
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.cursor = 'pointer';
              }}
            >
              <svg className="w-8 h-8 text-white/80 hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            
            <div 
              className="carousel-next-button absolute -right-4 top-1/2 -translate-y-1/2 w-12 h-14 flex items-center justify-center z-20 bg-black/40 rounded-lg hover:bg-black/60 transition-colors cursor-pointer"
              onClick={() => {
                // 直接使用DOM API触发点击事件确保正常工作
                const nextBtn = document.querySelector('.swiper-button-next');
                if (nextBtn) {
                  (nextBtn as HTMLElement).click();
                }
              }}
              aria-label="Next slide"
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.cursor = 'pointer';
              }}
            >
              <svg className="w-8 h-8 text-white/80 hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            
            {/* Swiper组件 */}
            <Swiper
              ref={swiperRef}
              modules={[Autoplay, Navigation]}
              spaceBetween={20}
              slidesPerView={1}
              loop={true}
              speed={1000}
              autoplay={{
                delay: 2000,  // 设置更长的延迟
                disableOnInteraction: false
              }}
              navigation={{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }}
              breakpoints={{
                // 当屏幕宽度大于等于640px
                640: {
                  slidesPerView: 2,
                  spaceBetween: 20
                },
                // 当屏幕宽度大于等于768px
                768: {
                  slidesPerView: 3,
                  spaceBetween: 20
                },
                // 当屏幕宽度大于等于1024px
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 20
                }
              }}
              className="w-full partners-carousel mx-auto"
            >
              {/* 添加隐藏的导航按钮，由Swiper内部使用 */}
              <div className="swiper-button-prev hidden"></div>
              <div className="swiper-button-next hidden"></div>
              
              {displayPartners.map((partner, index) => (
                <SwiperSlide key={`partner-${index}`}>
                  <div className="bg-black/30 rounded-lg p-6 flex items-center justify-center group hover:bg-black/50 transition-all duration-300 h-[160px]">
                    <div className="text-center">
                      {partner.logo ? (
                        <img 
                          src={partner.logo} 
                          alt={partner.name} 
                          className="h-16 w-auto mx-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
                          onError={() => handleImageError(index % loadedPartners.length)}
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-16 flex items-center justify-center">
                          <span className="text-gray-400">{partner.name || "Partner"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProfessionalPartnersSection; 